import React, { useMemo } from "react";
import styled, { DefaultTheme } from "styled-components/native";
import VDBText from "@components/atoms/Text";
import { SpaceType } from "@components/atoms/Spacer/types";
import { useTheme } from "styled-components/native";
import useScreenOptionsHooks from "../../../hooks/useScreenOptionsHooks";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import ThreeDotOptions from "./ThreeDotOptions";
import LineViewContainer from "@scenes/requests/utils/LineView";
import { Pressable, View } from "react-native";
import { TeamMember } from "@src/types/UserList";
import { Spacer } from "@components/atoms";
import { userStatus } from "../MoreScreen/utils";
import { handleEmailClick } from "@utils/product/productValidators";

type Column = {
  key: string;
  label: string;
  order: number;
  priceMode: number[];
};

type Props = {
  columns: Column[];
  users: TeamMember[];
  handleEvent: (event: string, user: TeamMember) => void;
};

const UsersSeatsList = ({ columns, users, handleEvent }: Props) => {
  const theme = useTheme();
  const { typography, t } = useScreenOptionsHooks();
  const userInfo = useUserInfo();
  const priceMode = userInfo.user?.price_mode;
  const activeColumns = useMemo(() => {
    return (columns || [])
      .filter((c) => c.priceMode.includes(priceMode))
      .sort((a, b) => a.order - b.order);
  }, [columns, priceMode]);

  const formatLastAccess = (lastAccessTime?: string | null): string => {
    if (!lastAccessTime) return "-";

    const now = new Date();
    const lastAccess = new Date(lastAccessTime);

    if (lastAccess.getTime() > now.getTime()) {
      return t("account.userList.accessTime.today.text", "Today");
    }
    const diffMs = now.getTime() - lastAccess.getTime();
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays <= 0) {
      return t("account.userList.accessTime.today.text", "Today");
    } else if (diffDays === 1) {
      return `${diffDays} ${t(
        "account.userList.accessTime.dayAgo.text",
        "day ago"
      )}`;
    }
    return `${diffDays} ${t(
      "account.userList.accessTime.daysAgo.text",
      "days ago"
    )}`;
  };

  const renderCell = (user: TeamMember, col: Column) => {
    switch (col.key) {
      case "name_email":
        return (
          <NameEmailCell>
            <VDBText type={typography["Body-M"]} color={theme["core-05"]}>
              {user.first_name + " " + user.last_name}
              {user.id === userInfo.user?.id ? " (You)" : ""}
            </VDBText>
            <Pressable onPress={() => handleEmailClick(t, user.email)}>
              <VDBText type={typography["Body-M"]} color={theme["main-04"]}>
                {user.email}
              </VDBText>
            </Pressable>
          </NameEmailCell>
        );
      case "role":
        return (
          <VDBText type={typography["Body-R"]} color={theme["core-05"]}>
            {user.is_admin_true === true
              ? t("account.userList.admin.text", "Admin")
              : t("account.userList.member.text", "Member")}
          </VDBText>
        );
      case "status":
        return (
          <StatusBadge status={userStatus(user)}>
            <VDBText
              type={typography["Body-M"]}
              color={
                userStatus(user) === "activated"
                  ? theme["success-01"]
                  : userStatus(user) === "limited_access"
                  ? theme["warn-01"]
                  : theme["core-05"]
              }
            >
              {userStatus(user) === "activated"
                ? t("account.userList.active.text", "Full Access")
                : userStatus(user) === "invited"
                ? t("account.userList.invited.text", "Invited")
                : userStatus(user) === "limited_access"
                ? t("account.userList.limitedAccess.text", "Limited Access")
                : t("account.userList.invited.text", "Invited")}
            </VDBText>
          </StatusBadge>
        );
      case "last_active":
        return (
          <VDBText type={typography["Body-R"]} color={theme["core-05"]}>
            {formatLastAccess(user?.last_access_time)}
          </VDBText>
        );
      case "actions":
        return (
          <ThreeDotOptions
            key={user.id}
            user={user}
            handleEvent={handleEvent}
          />
        );
      default:
        return null;
    }
  };

  return theme.isMobile ? (
    users.map((u, index) => (
      <>
        <CardContainer key={`${u.id}`}>
          <TopContainer>
            <NameEmailCell>
              <VDBText type={typography["Body-M"]} color={theme["core-05"]}>
                {u.first_name + " " + u.last_name}
                {u.id === userInfo.user?.id ? " (You)" : ""}
              </VDBText>
              <Spacer size={SpaceType.ExtraSmall} />
              <VDBText
                type={typography["Body-M"]}
                color={theme["main-04"]}
                isUnderlined={false}
              >
                {u.email}
              </VDBText>
            </NameEmailCell>
            <StatusBadge status={userStatus(u)}>
              <VDBText
                type={typography["Body-M"]}
                color={
                  userStatus(u) === "activated"
                    ? theme["success-01"]
                    : userStatus(u) === "limited_access"
                    ? theme["warn-01"]
                    : theme["core-05"]
                }
              >
                {userStatus(u) === "activated"
                  ? t("account.userList.active.text", "Full Access")
                  : userStatus(u) === "invited"
                  ? t("account.userList.invited.text", "Invited")
                  : userStatus(u) === "limited_access"
                  ? t("account.userList.limitedAccess.text", "Limited Access")
                  : t("account.userList.invited.text", "Invited")}
              </VDBText>
            </StatusBadge>
          </TopContainer>
          <BottomContainer>
            <BottomCell>
              <RoleCell>
                <TextContainer>
                  <VDBText type={typography["Body-R"]} color={theme["core-06"]}>
                    {t("account.userList.header.role.text", "Role")}
                  </VDBText>
                </TextContainer>
                <Spacer size={SpaceType.Double} />
                <VDBText type={typography["Body-M"]} color={theme["core-05"]}>
                  {u.is_admin_true === true
                    ? t("account.userList.admin.text", "Admin")
                    : t("account.userList.member.text", "Member")}
                </VDBText>
              </RoleCell>
              <Spacer size={SpaceType.Basic} />
              <RoleCell>
                <TextContainer>
                  <VDBText type={typography["Body-R"]} color={theme["core-06"]}>
                    {t(
                      "account.userList.header.lastActive.text",
                      "Last Active"
                    )}
                  </VDBText>
                </TextContainer>
                <Spacer size={SpaceType.Double} />
                <VDBText type={typography["Body-M"]} color={theme["core-05"]}>
                  {formatLastAccess(u?.last_access_time)}
                </VDBText>
              </RoleCell>
            </BottomCell>
            <ThreeDotOptions
              user={u}
              handleEvent={handleEvent}
              lastItem={index === users.length - 1}
            />
          </BottomContainer>
        </CardContainer>
        {index !== users.length - 1 && <Spacer size={SpaceType.Basic} />}
      </>
    ))
  ) : (
    <TableContainer>
      <HeaderRow>
        {activeColumns.map((c) => (
          <HeaderCell
            key={c.key}
            flex={
              c.key === "name_email"
                ? 0.5
                : c.key === "actions"
                ? 0.1
                : c.key === "role"
                ? 0.2
                : 0.3
            }
          >
            <VDBText type={typography["Body-M"]} color={theme["core-05"]}>
              {c.label}
            </VDBText>
          </HeaderCell>
        ))}
      </HeaderRow>
      <View style={{ paddingHorizontal: SpaceType.Double }}>
        <LineViewContainer background="core-03" />
      </View>
      {users.map((u, index) => (
        <>
          <BodyRow key={u.id}>
            {activeColumns.map((c) => (
              <BodyCell
                key={`${u.id}-${c.key}`}
                flex={
                  c.key === "name_email"
                    ? 0.5
                    : c.key === "actions"
                    ? 0.1
                    : c.key === "role"
                    ? 0.2
                    : 0.3
                }
              >
                {renderCell(u, c)}
              </BodyCell>
            ))}
          </BodyRow>
          {index !== users.length - 1 && (
            <View style={{ paddingHorizontal: SpaceType.Double }}>
              <LineViewContainer background="core-03" />
            </View>
          )}
        </>
      ))}
    </TableContainer>
  );
};

const TableContainer = styled.View`
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-01"]};
  border-radius: ${SpaceType.Double}px;
`;

const HeaderRow = styled.View`
  flex-direction: row;
  padding: 26px ${SpaceType.Double}px;
  border-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-03"]};
`;

const HeaderCell = styled.View<{ flex?: number }>`
  flex: ${({ flex }) => flex || 1};
`;

const BodyRow = styled.View`
  flex-direction: row;
  padding: 14px ${SpaceType.Double}px;
  border-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-02"]};
`;

const BodyCell = styled.View<{ flex?: number }>`
  flex: ${({ flex }) => flex || 1};
  justify-content: center;
`;

const NameEmailCell = styled.View``;

const CardContainer = styled.View`
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-01"]};
  border-radius: ${SpaceType.Double}px;
  padding: ${SpaceType.Double}px;
`;

const TopContainer = styled.View`
  flex-direction: row;
  justify-content: space-between;
  padding-bottom: ${SpaceType.Basic}px;
  border-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-03"]};
  border-bottom-width: 1px;
`;

const StatusBadge = styled.View<{ status: string }>`
  align-self: flex-start;
  padding: 2px 8px;
  border-radius: 24px;
  background-color: ${({ status, theme }) =>
    status === "activated"
      ? theme["success-02"]
      : status === "limited_access"
      ? theme["warn-02"]
      : theme["core-02"]};
`;

const BottomContainer = styled.View`
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-end;
  padding-top: ${SpaceType.Basic}px;
`;

const RoleCell = styled.View`
  flex-direction: row;
  align-items: center;
`;

const TextContainer = styled.View`
  min-width: 70px;
`;

const BottomCell = styled.View`
  flex-direction: column;
`;

export default UsersSeatsList;
